/**
 * anglicism-site-scan.js — whole-site scan orchestration for /anglicism-checker.
 *
 * Shared between:
 *   - the browser UI (resources/views/tools/anglicism-checker.blade.php) via
 *     window.AnglicismSiteScan
 *   - the Node test harness (tests/anglicism/site-scan.cjs) via module.exports
 *
 * Design: this module owns DISCOVERY (sitemap / same-host link crawl),
 * the fetch worker pool, per-page bookkeeping and the aggregated report.
 * Everything environmental is injected — fetchPage / extract / analyze — so the
 * module has NO DOM, NO network and NO dictionary dependency of its own, and the
 * whole state machine is exercisable in Node with fixture functions.
 *
 * Who fetches is decided by the CALLER: the blade passes a direct fetch for the
 * tool's own origin (a same-origin scan costs the server nothing beyond normal
 * cached page traffic) and a server-proxied fetch for foreign origins that
 * refuse CORS — with the proxy's page cap enforced here via maxPages.
 */
(function () {
    'use strict';

    /* ─────────── URL helpers (pure) ─────────── */

    function normalizeStartUrl(raw) {
        let s = String(raw || '').trim();
        if (!s) return null;
        // An explicit non-http scheme is a refusal, not something to "fix" by
        // prefixing https:// (that would turn ftp://x into https://ftp://x).
        if (/^[a-z][a-z0-9+.-]*:/i.test(s) && !/^https?:\/\//i.test(s)) return null;
        if (!/^https?:\/\//i.test(s)) s = 'https://' + s;
        let u;
        try { u = new URL(s); } catch (_) { return null; }
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
        u.hash = '';
        return u.href;
    }

    // Host match with www-tolerance: scanning `example.com` must accept
    // sitemap entries written as `www.example.com` and vice versa.
    function hostKey(hostname) {
        return String(hostname || '').toLowerCase().replace(/^www\./, '');
    }
    function sameSite(urlA, urlB) {
        try { return hostKey(new URL(urlA).hostname) === hostKey(new URL(urlB).hostname); }
        catch (_) { return false; }
    }

    function normalizePageUrl(raw, base) {
        let u;
        try { u = new URL(raw, base); } catch (_) { return null; }
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
        u.hash = '';
        return u.href;
    }

    /* ─────────── sitemap XML (pure, regex — no DOM so Node can run it) ─────────── */

    function xmlDecode(s) {
        return String(s)
            .replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, '$1')
            .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
            .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(+d))
            .replace(/&amp;/g, '&')
            .trim();
    }

    /**
     * Returns { kind: 'index'|'pages'|'none', urls: string[] }.
     * `<sitemapindex>` → child sitemap URLs; `<urlset>` → page URLs, document
     * order preserved (sitemap order is deliberate — homepage and primary pages
     * first — and capping should keep the front, not a priority-shuffled slice).
     */
    function parseSitemapXml(xml) {
        const s = String(xml || '');
        const head = s.slice(0, 4000).toLowerCase();
        const kind = head.includes('<sitemapindex') ? 'index'
            : head.includes('<urlset') ? 'pages' : 'none';
        if (kind === 'none') return { kind, urls: [] };
        const urls = [];
        const re = /<loc[^>]*>([\s\S]*?)<\/loc>/gi;
        let m;
        while ((m = re.exec(s)) !== null) {
            const u = xmlDecode(m[1]);
            if (u) urls.push(u);
        }
        return { kind, urls };
    }

    /* ─────────── link extraction for the no-sitemap crawl fallback ─────────── */

    // Deliberately dumb: href="..." out of raw HTML. The crawl fallback only
    // needs same-host <a> targets; a full parser buys nothing here.
    function extractLinks(html, baseUrl) {
        const out = [];
        const seen = new Set();
        const re = /<a\b[^>]*?href\s*=\s*("([^"]*)"|'([^']*)')/gi;
        let m;
        const source = String(html || '');
        while ((m = re.exec(source)) !== null) {
            const raw = xmlDecode(m[2] != null ? m[2] : m[3] || '');
            if (!raw || raw.startsWith('mailto:') || raw.startsWith('tel:') || raw.startsWith('javascript:')) continue;
            const abs = normalizePageUrl(raw, baseUrl);
            if (!abs || !sameSite(abs, baseUrl)) continue;
            // Skip obvious non-page assets.
            if (/\.(png|jpe?g|gif|webp|svg|ico|css|js|mjs|json|xml|pdf|zip|mp[34]|webm|wasm|woff2?)(\?|$)/i.test(abs)) continue;
            if (!seen.has(abs)) { seen.add(abs); out.push(abs); }
        }
        return out;
    }

    /* ─────────── discovery ─────────── */

    /**
     * Find the page list for a site.
     *   opts: { startUrl, fetchText(url) → Promise<{ok,status,text}>, maxPages,
     *           maxChildSitemaps? }
     * Returns { source: 'sitemap'|'crawl'|'single', urls, truncated, sitemapUrl? }.
     * 'crawl' means: no usable sitemap — caller should seed with startUrl and let
     * the scan feed the queue from page links.
     */
    async function discoverPages(opts) {
        const startUrl = normalizeStartUrl(opts.startUrl);
        if (!startUrl) throw new Error('bad_start_url');
        const maxPages = Math.max(1, opts.maxPages || 100);
        const maxChildren = Math.max(1, opts.maxChildSitemaps || 10);
        const origin = new URL(startUrl).origin;

        const looksLikeSitemap = /\.xml(\?|$)/i.test(startUrl) || /sitemap/i.test(new URL(startUrl).pathname);
        const candidates = looksLikeSitemap
            ? [startUrl, origin + '/sitemap.xml']
            : [origin + '/sitemap.xml'];

        for (const smUrl of candidates) {
            let res;
            try { res = await opts.fetchText(smUrl); } catch (_) { continue; }
            if (!res || !res.ok) continue;
            const parsed = parseSitemapXml(res.text);
            if (parsed.kind === 'pages' && parsed.urls.length) {
                const urls = dedupeSameSite(parsed.urls, startUrl, maxPages);
                if (urls.list.length) {
                    return { source: 'sitemap', urls: urls.list, truncated: urls.truncated, sitemapUrl: smUrl };
                }
            }
            if (parsed.kind === 'index' && parsed.urls.length) {
                const collected = [];
                let truncated = false;
                let childrenTried = 0;
                for (const child of parsed.urls) {
                    if (childrenTried >= maxChildren) { truncated = true; break; }
                    if (collected.length >= maxPages) { truncated = true; break; }
                    childrenTried++;
                    let cres;
                    try { cres = await opts.fetchText(child); } catch (_) { continue; }
                    if (!cres || !cres.ok) continue;
                    const cparsed = parseSitemapXml(cres.text);
                    if (cparsed.kind !== 'pages') continue;
                    for (const u of cparsed.urls) collected.push(u);
                }
                const urls = dedupeSameSite(collected, startUrl, maxPages);
                if (urls.list.length) {
                    return {
                        source: 'sitemap', urls: urls.list,
                        truncated: truncated || urls.truncated, sitemapUrl: smUrl,
                    };
                }
            }
        }

        // No sitemap → crawl from the start page (or single page if it was deep).
        return { source: 'crawl', urls: [startUrl], truncated: false };
    }

    function dedupeSameSite(rawUrls, siteUrl, maxPages) {
        const seen = new Set();
        const list = [];
        let truncated = false;
        for (const raw of rawUrls) {
            const u = normalizePageUrl(raw, siteUrl);
            if (!u || !sameSite(u, siteUrl)) continue;
            if (seen.has(u)) continue;
            seen.add(u);
            if (list.length >= maxPages) { truncated = true; break; }
            list.push(u);
        }
        return { list, truncated };
    }

    /* ─────────── incremental aggregation ─────────── */

    function newAggState() {
        return {
            pages: 0, words: 0, hits: 0, weightedScore: 0,
            tiers: { avoidable: 0, nuance: 0, needed: 0 },
            lemmas: new Map(),   // w → {w, count, pages, tier, sev, cat, alt}
        };
    }

    function aggAddPage(state, page) {
        state.pages++;
        state.words += page.words;
        state.weightedScore += page.score * page.words;
        const seenHere = new Set();
        for (const h of page.hits) {
            state.hits++;
            const tier = h.tier === 'avoidable' || h.tier === 'nuance' || h.tier === 'needed' ? h.tier : 'avoidable';
            state.tiers[tier]++;
            const key = String(h.w || '').toLowerCase();
            if (!key) continue;
            let rec = state.lemmas.get(key);
            if (!rec) {
                rec = { w: h.w, count: 0, pages: 0, tier, sev: h.sev || '', cat: h.cat || '', alt: h.alt || null };
                state.lemmas.set(key, rec);
            }
            rec.count++;
            if (!seenHere.has(key)) { seenHere.add(key); rec.pages++; }
        }
    }

    function aggFinish(state) {
        const score = state.words > 0 ? Math.round(state.weightedScore / state.words) : 100;
        const grade = score >= 90 ? 'A' : score >= 75 ? 'B' : score >= 60 ? 'C' : score >= 40 ? 'D' : 'F';
        const top = Array.from(state.lemmas.values())
            .sort((a, b) => b.count - a.count || b.pages - a.pages || (a.w < b.w ? -1 : 1));
        return {
            score, grade,
            pages: state.pages, words: state.words, hits: state.hits,
            tiers: { avoidable: state.tiers.avoidable, nuance: state.tiers.nuance, needed: state.tiers.needed },
            top,
        };
    }

    /* ─────────── the scan itself ─────────── */

    /**
     * opts:
     *   startUrl        string — site root / any page / sitemap URL
     *   fetchPage(url)  → Promise<{ok, status, html, finalUrl?}> (html = text for xml too)
     *   extract(html, url) → {title, lang, text, words} — caller's DOM extractor
     *   analyze(text, lang) → Promise<{score, grade, words, hits:[{w,tier,sev,cat,alt}]}>
     *   maxPages        number (hard cap, discovery AND crawl)
     *   concurrency     number (default 4)
     *   minWords        number — pages thinner than this are skipped (default 15)
     *   lang            'auto' | dict lang — 'auto' locks to the first analyzed page's lang
     *   keepTexts       bool — retain extracted text per page for drill-down (default true)
     *   maxTextKeep     number — per-page text cap in chars when keeping (default 150k)
     *   onProgress(evt) optional
     *   shouldStop()    optional → truthy aborts between steps
     *
     * Proxy-path overrides (foreign origins whose pages the browser cannot read
     * because of CORS — the server does the fetching and text extraction):
     *   discoverOverride(startUrl, maxPages) → Promise<{source, urls, truncated, sitemapUrl?}>
     *   fetchExtracted(url) → Promise<{ok, status, title, lang, text, words}> —
     *       replaces the fetchPage+extract pair for PAGES (discovery still uses
     *       discoverOverride). Crawl self-feeding is unavailable on this path.
     *
     * Resolves { source, sitemapUrl, urlsDiscovered, truncated, aborted,
     *            lang, pages[], failed[], skipped[], agg, elapsedMs }.
     */
    async function runSiteScan(opts) {
        const t0 = Date.now();
        const emit = typeof opts.onProgress === 'function' ? opts.onProgress : function () {};
        const stop = typeof opts.shouldStop === 'function' ? opts.shouldStop : function () { return false; };
        const maxPages = Math.max(1, opts.maxPages || 100);
        const concurrency = Math.max(1, Math.min(8, opts.concurrency || 4));
        const minWords = opts.minWords == null ? 15 : opts.minWords;
        const keepTexts = opts.keepTexts !== false;
        const maxTextKeep = opts.maxTextKeep || 150000;

        const startUrl = normalizeStartUrl(opts.startUrl);
        if (!startUrl) throw new Error('bad_start_url');

        const discovery = opts.discoverOverride
            ? await opts.discoverOverride(startUrl, maxPages)
            : await discoverPages({
                startUrl,
                fetchText: async (u) => {
                    const r = await opts.fetchPage(u);
                    return r && { ok: r.ok, status: r.status, text: r.html };
                },
                maxPages,
            });
        if (stop()) return finish(true);

        const crawlMode = discovery.source === 'crawl';
        const queue = discovery.urls.slice();
        const queued = new Set(queue);
        let discoveredTotal = crawlMode ? null : queue.length;   // null = open-ended
        emit({ type: 'discovered', source: discovery.source, count: queue.length, total: discoveredTotal, truncated: discovery.truncated });

        const pages = [];
        const failed = [];
        const skipped = [];
        const agg = newAggState();
        let scanLang = opts.lang && opts.lang !== 'auto' ? opts.lang : null;
        let processedCount = 0;
        let aborted = false;
        let crawlOverflow = false;
        let retried = 0;

        // One retry for transient shapes only — a dropped connection (throw) or
        // a 5xx. A 4xx is a verdict, retrying it just doubles the wait. (The
        // 2026-07-15 dogfood lost 9 consecutive pages to one local reset burst;
        // all nine were rescuable.)
        async function fetchWithRetry(fn, url) {
            try {
                const res = await fn(url);
                if (res && !res.ok && res.status >= 500) throw Object.assign(new Error('http_' + res.status), { status: res.status, res });
                return res;
            } catch (e1) {
                if (e1 && e1.status && e1.status < 500) return e1.res;   // non-transient HTTP → no retry
                await new Promise(r => setTimeout(r, 400));
                retried++;
                if (e1 && e1.res) {
                    const res2 = await fn(url);
                    return (res2 && res2.ok) ? res2 : e1.res;            // keep the FIRST verdict if retry also fails
                }
                return fn(url);                                          // network throw → let a second throw surface
            }
        }

        async function processUrl(url) {
            emit({ type: 'fetch', url, done: processedCount, total: discoveredTotal || queued.size });
            let ext;
            if (opts.fetchExtracted) {
                let res;
                try { res = await fetchWithRetry(opts.fetchExtracted, url); }
                catch (e) { failed.push({ url, reason: 'network', detail: String(e && e.message || e).slice(0, 120) }); return; }
                if (!res || !res.ok) { failed.push({ url, reason: 'http_' + (res && res.status || 0) }); return; }
                ext = { title: res.title, lang: res.lang, text: res.text, words: res.words };
            } else {
                let res;
                try { res = await fetchWithRetry(opts.fetchPage, url); }
                catch (e) { failed.push({ url, reason: 'network', detail: String(e && e.message || e).slice(0, 120) }); return; }
                if (!res || !res.ok) { failed.push({ url, reason: 'http_' + (res && res.status || 0) }); return; }

                // Crawl mode feeds itself from page links until the cap. A link
                // refused by the cap IS truncation — remember it, or a fully-drained
                // queue would report the scan as complete.
                if (crawlMode) {
                    for (const link of extractLinks(res.html, res.finalUrl || url)) {
                        if (queued.has(link)) continue;
                        if (queued.size >= maxPages) { crawlOverflow = true; break; }
                        queued.add(link); queue.push(link);
                    }
                }

                try { ext = opts.extract(res.html, url); }
                catch (e) { failed.push({ url, reason: 'extract', detail: String(e && e.message || e).slice(0, 120) }); return; }
            }
            if (!ext || !ext.text || ext.words < minWords) {
                skipped.push({ url, reason: 'thin', words: ext ? ext.words : 0 });
                return;
            }

            const pageLang = (ext.lang || '').slice(0, 2).toLowerCase() || null;
            if (scanLang && pageLang && pageLang !== scanLang) {
                skipped.push({ url, reason: 'other_lang', lang: pageLang, title: ext.title });
                return;
            }

            let result;
            try { result = await opts.analyze(ext.text, scanLang || pageLang || 'auto'); }
            catch (e) { failed.push({ url, reason: 'analyze', detail: String(e && e.message || e).slice(0, 120) }); return; }
            if (!result) { failed.push({ url, reason: 'analyze' }); return; }
            if (!scanLang) scanLang = result.lang || pageLang || null;

            const page = {
                url,
                title: ext.title || url,
                lang: pageLang || scanLang,
                words: result.words,
                score: result.score,
                grade: result.grade,
                hits: result.hits,
                tiers: countTiers(result.hits),
                text: keepTexts ? String(ext.text).slice(0, maxTextKeep) : null,
            };
            pages.push(page);
            aggAddPage(agg, page);
            // The per-page hit list has served aggregation; keep only the light
            // top-lemmas for the row UI so 1000-page scans stay flat in memory.
            page.topLocal = topLocal(result.hits, 5);
            page.hits = null;

            emit({
                type: 'page', url, title: page.title, score: page.score, grade: page.grade,
                words: page.words, tiers: page.tiers,
                done: processedCount + 1, total: discoveredTotal || queued.size,
            });
        }

        // Worker pool over a shared cursor — crawl mode may grow `queue` while
        // workers run, so the cursor is re-checked each iteration.
        let cursor = 0;
        async function worker() {
            while (true) {
                if (stop()) { aborted = true; return; }
                if (cursor >= queue.length || processedCount >= maxPages) return;
                const url = queue[cursor++];
                await processUrl(url);
                processedCount++;
            }
        }
        const workers = [];
        for (let i = 0; i < concurrency; i++) workers.push(worker());
        await Promise.all(workers);

        return finish(aborted);

        function finish(wasAborted) {
            const aggOut = aggFinish(agg);
            return {
                source: discovery ? discovery.source : 'none',
                sitemapUrl: discovery && discovery.sitemapUrl || null,
                urlsDiscovered: queue.length,
                truncated: !!(discovery && discovery.truncated) || queue.length > processedCount || crawlOverflow,
                aborted: !!wasAborted,
                lang: scanLang,
                pages, failed, skipped,
                retried,
                agg: aggOut,
                elapsedMs: Date.now() - t0,
            };
        }
    }

    function countTiers(hits) {
        const t = { avoidable: 0, nuance: 0, needed: 0 };
        for (const h of hits) {
            if (h.tier === 'nuance') t.nuance++;
            else if (h.tier === 'needed') t.needed++;
            else t.avoidable++;
        }
        return t;
    }

    function topLocal(hits, n) {
        const m = new Map();
        for (const h of hits) {
            const k = String(h.w || '').toLowerCase();
            if (!k) continue;
            m.set(k, (m.get(k) || 0) + 1);
        }
        return Array.from(m.entries())
            .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
            .slice(0, n)
            .map(([w, count]) => ({ w, count }));
    }

    /* ─────────── exports (CSV kept here so Node tests cover it) ─────────── */

    function csvCell(v) {
        const s = String(v == null ? '' : v);
        return /[",\n;]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    }

    function reportToCsv(report) {
        const rows = [['url', 'title', 'lang', 'words', 'score', 'grade', 'hits_total', 'avoidable', 'nuance', 'needed', 'top_words']];
        const sorted = report.pages.slice().sort((a, b) => a.score - b.score);
        for (const p of sorted) {
            const hitsTotal = p.tiers.avoidable + p.tiers.nuance + p.tiers.needed;
            rows.push([
                p.url, p.title, p.lang || '', p.words, p.score, p.grade, hitsTotal,
                p.tiers.avoidable, p.tiers.nuance, p.tiers.needed,
                (p.topLocal || []).map(t => t.w + '×' + t.count).join(' '),
            ]);
        }
        for (const f of report.failed) rows.push([f.url, '(' + f.reason + ')', '', '', '', '', '', '', '', '', '']);
        // Excel needs the BOM to read UTF-8 Cyrillic.
        return '\uFEFF' + rows.map(r => r.map(csvCell).join(',')).join('\r\n');
    }

    const api = {
        normalizeStartUrl, sameSite, normalizePageUrl,
        parseSitemapXml, extractLinks,
        discoverPages, runSiteScan,
        newAggState, aggAddPage, aggFinish,
        reportToCsv,
    };
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (typeof window !== 'undefined') window.AnglicismSiteScan = api;
})();
